// Scoping happens through where a setting lives, never through a picker (bible
// §18, rewritten 2026-08-20). This module holds the part of that model that is
// not React: what the scopes are called in an owner's words, which keys may
// actually be overridden at each one, and what a setting affects.

// The one editing context the Admin Settings tree has: the outlet layer — the
// value every terminal, station and printer inherits. There is no manual scope
// selector any more (§18); device-scoped behavior is edited on the device's
// own surface.
export const OUTLET_SCOPE = { type: "GLOBAL", id: "", label: "This Outlet" };

/**
 * The scope a ConfigMetadata declares is the DEEPEST scope that key resolves at.
 * config_resolver applies the precedence ROLE > PRINTER/TERMINAL/STATION > OUTLET
 * > GLOBAL > registry default, but it will happily return an override written at
 * any scope for any key — so the resolver is not the guard. The guard is here: a
 * per-station override on `restaurant_name` would be stored, would come back from
 * /settings/resolved, and would still never reach the bill, because the only
 * consumer that resolves per station is the KDS. Offering that control is exactly
 * the fake configuration §46 forbids.
 *
 * Registry facts this encodes (backend/app/services/config_registry.py):
 *   station-scoped → the five kds_* keys, read by GET /api/kds/settings?station_id=
 *   printer-scoped → currency_render, printer_cut_mode, cash_drawer_enabled
 *   outlet/global  → everything else; there is no per-device consumer.
 */
export const scopeOfMeta = meta => String(meta?.scope || "outlet").toUpperCase();

/**
 * Can this key be overridden in the scope the header is on?
 *
 * "This Outlet" always yes — that is the value itself, not an override. Anything
 * deeper only when the registry says the key resolves there.
 */
export function canOverrideAt(meta, scopeType) {
  if (!scopeType || scopeType === "GLOBAL" || scopeType === "OUTLET") return true;
  return scopeOfMeta(meta) === scopeType;
}

/** Human-readable name for a raw source_scope from the resolver. */
export const humanScope = raw => {
  const map = {
    GLOBAL: "Outlet", OUTLET: "Outlet", TERMINAL: "Terminal",
    STATION: "Kitchen station", PRINTER: "Printer", ROLE: "Role", DEFAULT: "System",
  };
  return map[raw] || raw;
};

/** What the picker calls a scope when it has no name from the server yet. */
export const SCOPE_GROUP_TITLES = {
  GLOBAL: "This Outlet",
  TERMINAL: "POS Terminals",
  STATION: "Kitchen Stations",
  PRINTER: "Printers",
};

/** The one word for a scope in a sentence — "not overridable per kitchen station". */
export const scopeNoun = type => ({
  TERMINAL: "terminal", STATION: "kitchen station", PRINTER: "printer",
}[type] || "outlet");

/**
 * `affected_outputs` → the short "Affects Bill / KOT" tail of a status line.
 * Print and preview of the same document are one thing to an owner.
 */
const OUTPUT_LABELS = {
  bill_print: "Bill", bill_preview: "Bill",
  kot_print: "KOT", kot_preview: "KOT",
  ui_kds: "KDS", ui_pos: "POS",
  ui_reports: "Reports", exports: "Reports",
  ui_admin: "Admin",
};

export function affectsLabel(outputs) {
  const seen = [];
  for (const output of Array.isArray(outputs) ? outputs : []) {
    const label = OUTPUT_LABELS[output];
    if (label && !seen.includes(label)) seen.push(label);
  }
  return seen.join(" / ");
}

/**
 * The one line every row owes the owner: inherited, overridden here, or default.
 *
 * At the outlet the resolver's `overridden` flag does not mean "an override
 * exists" — it means the value came out of restaurant_settings rather than out of
 * the registry default (config_resolver line 107). Calling that "override active"
 * told the owner a per-scope override existed when none did, so the outlet says
 * "Set at this outlet" and only a device scope talks about overrides.
 */
export function inheritanceStatus(contextValue, scopeType, scopeLabel) {
  const source = contextValue?.source_scope || "DEFAULT";
  const overridden = !!contextValue?.overridden;

  if (scopeType === "GLOBAL" || scopeType === "OUTLET") {
    if (overridden) return { text: "Set at this outlet", overriddenHere: false };
    if (source === "DEFAULT") return { text: "System default", overriddenHere: false };
    return { text: `Inherited from ${humanScope(source)}`, overriddenHere: false };
  }

  if (overridden) return { text: `Override active on ${scopeLabel}`, overriddenHere: true };
  if (source === "DEFAULT") return { text: "System default", overriddenHere: false };
  return { text: `Inherited from ${humanScope(source)}`, overriddenHere: false };
}
